import { useEffect, useState, type ReactElement } from 'react';
import type { PatientRecordFields, PatientRecordUpdate, PatientSex } from '@zenda/api-contract';
import { CampoData } from './CampoData';
import { toDateInput } from '../lib/format';
import {
  erroDoCep,
  erroDoCpf,
  erroDoEmail,
  erroDoTelefone,
  mascararCep,
  mascararCpf,
  mascararTelefone,
} from '../lib/mascaras';
import { useT } from '../lib/i18n';

type Chave = Exclude<keyof PatientRecordFields, 'sex' | 'insuranceValidUntil'>;

const identidade = (v: string): string => v;

interface Formato {
  readonly tipo?: 'text' | 'email' | 'tel';
  readonly modo?: 'numeric' | 'tel' | 'email';
  readonly mascara?: (v: string) => string;
  /** Nulo quando o valor serve; senão, o que corrigir. Valor com erro não é salvo. */
  readonly validar?: (v: string) => string | null;
  readonly max?: number;
  readonly obrigatorio?: string;
}

/**
 * Um campo de texto que salva ao sair dele, e só se mudou. Segue o valor do servidor
 * enquanto não está sendo editado, como a anotação.
 */
function Campo({
  id,
  rotulo,
  valor,
  salvar,
  tipo = 'text',
  modo,
  mascara = identidade,
  validar,
  max = 200,
  obrigatorio,
}: {
  readonly id: string;
  readonly rotulo: string;
  readonly valor: string | null;
  readonly salvar: (v: string) => void;
} & Formato): ReactElement {
  const [texto, setTexto] = useState(mascara(valor ?? ''));
  const [editando, setEditando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  useEffect(() => {
    if (!editando) setTexto(mascara(valor ?? ''));
  }, [valor, editando, mascara]);

  return (
    <div className="field">
      <label className="label" htmlFor={id}>
        {rotulo}
      </label>
      <input
        id={id}
        className={`input input--sm${erro === null ? '' : ' is-invalid'}`}
        type={tipo}
        inputMode={modo}
        value={texto}
        maxLength={max}
        aria-invalid={erro !== null}
        aria-describedby={erro === null ? undefined : `${id}-erro`}
        onFocus={() => setEditando(true)}
        onChange={(e) => {
          setTexto(mascara(e.target.value));
          setErro(null);
        }}
        onBlur={() => {
          setEditando(false);
          const limpo = texto.trim();
          const problema =
            limpo === '' && obrigatorio !== undefined
              ? obrigatorio
              : limpo === ''
                ? null
                : (validar?.(limpo) ?? null);
          setErro(problema);
          if (problema === null && limpo !== mascara(valor ?? '')) salvar(limpo);
        }}
      />
      {erro === null ? null : (
        <span className="form__error" id={`${id}-erro`}>
          {erro}
        </span>
      )}
    </div>
  );
}

const UFS = new Set(
  'AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS RO RR SC SP SE TO'.split(' '),
);
const mascararUf = (v: string): string =>
  v
    .replace(/[^a-zA-Z]/g, '')
    .slice(0, 2)
    .toUpperCase();
const erroDaUf = (v: string): string | null =>
  UFS.has(v) ? null : 'UF inválida. Ex.: SP, RJ, MG.';

/** Identificação, contato, endereço, convênio e origem: tudo opcional (ADR 0028). */
export function FichaEstendida({
  nome,
  telefone,
  telefoneTravado,
  ficha,
  salvar,
  erro,
}: {
  readonly nome: string;
  /** Já formatado para exibição. */
  readonly telefone: string;
  readonly telefoneTravado: boolean;
  readonly ficha: PatientRecordFields;
  readonly salvar: (mudanca: PatientRecordUpdate) => void;
  readonly erro: string | null;
}): ReactElement {
  const t = useT();
  const f = t.patients.ficha;
  const campo = (chave: Chave, extra: Formato = {}) => (
    <Campo
      key={chave}
      id={`pf-${chave}`}
      rotulo={f.campos[chave]}
      valor={ficha[chave]}
      salvar={(v) => salvar({ [chave]: v })}
      {...extra}
    />
  );

  return (
    <section className="dsec">
      {erro === null ? null : <p className="form__error">{erro}</p>}

      <details className="pficha">
        <summary className="dsec__title">{f.grupos.identificacao}</summary>
        <Campo
          id="pf-nome"
          rotulo={f.campos.nome}
          valor={nome}
          max={120}
          obrigatorio={f.nomeObrigatorio}
          salvar={(v) => salvar({ displayName: v })}
        />
        {campo('socialName')}
        <div className="field">
          <label className="label" htmlFor="pf-sex">
            {f.campos.sex}
          </label>
          <select
            id="pf-sex"
            className="select select--sm"
            value={ficha.sex ?? ''}
            onChange={(e) => salvar({ sex: (e.target.value || null) as PatientSex | null })}
          >
            <option value="">{f.naoInformado}</option>
            <option value="F">{f.sexos.F}</option>
            <option value="M">{f.sexos.M}</option>
            <option value="I">{f.sexos.I}</option>
          </select>
        </div>
        {campo('cpf', { modo: 'numeric', mascara: mascararCpf, validar: erroDoCpf })}
      </details>

      <details className="pficha">
        <summary className="dsec__title">{f.grupos.contato}</summary>
        {telefoneTravado ? (
          <div className="field">
            <span className="label">{f.campos.whatsapp}</span>
            <span className="num">{telefone}</span>
            <span className="dsec__hint">{f.telefoneTravado}</span>
          </div>
        ) : (
          <Campo
            id="pf-whatsapp"
            rotulo={f.campos.whatsapp}
            valor={telefone}
            tipo="tel"
            modo="tel"
            mascara={mascararTelefone}
            validar={erroDoTelefone}
            obrigatorio={f.telefoneObrigatorio}
            salvar={(v) => salvar({ phone: v })}
          />
        )}
        {campo('email', { tipo: 'email', modo: 'email', validar: erroDoEmail, max: 160 })}
        {campo('phoneSecondary', {
          tipo: 'tel',
          modo: 'tel',
          mascara: mascararTelefone,
          validar: erroDoTelefone,
        })}
      </details>

      <details className="pficha">
        <summary className="dsec__title">{f.grupos.endereco}</summary>
        {campo('cep', { modo: 'numeric', mascara: mascararCep, validar: erroDoCep })}
        {campo('street')}
        <div className="form__row">
          {campo('streetNumber')}
          {campo('complement')}
        </div>
        {campo('district')}
        <div className="form__row">
          {campo('city')}
          {campo('uf', { mascara: mascararUf, validar: erroDaUf, max: 2 })}
        </div>
      </details>

      <details className="pficha">
        <summary className="dsec__title">{f.grupos.convenio}</summary>
        {campo('insurer')}
        {campo('insurancePlan')}
        {campo('insuranceCardNumber')}
        <div className="field">
          <label className="label" htmlFor="pf-validade">
            {f.campos.insuranceValidUntil}
          </label>
          <CampoData
            id="pf-validade"
            size="sm"
            value={toDateInput(ficha.insuranceValidUntil)}
            onChange={(d) => salvar({ insuranceValidUntil: d === '' ? null : d })}
          />
        </div>
      </details>

      <details className="pficha">
        <summary className="dsec__title">{f.grupos.origem}</summary>
        {campo('referralSource')}
      </details>
    </section>
  );
}
